import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Pause, Play } from 'lucide-react';
import { api } from '../api/client';
import { Badge, ReachDot, EmptyState, ErrorState, Skeleton, useFetch } from '../components/ui';
import { TimeseriesChart } from '../components/charts';

/** Mirrors plugins/voip/src/appliance-lines.ts */
interface ApplianceLine {
  id: string;
  name: string;
  type: 'trunk' | 'extension' | 'port';
  online: boolean;
  state: string;
  activity: 'idle' | 'dialling' | 'incoming' | 'active' | 'unknown';
  number: string | null;
  signal: number | null;
  carrier: string | null;
  gsm?: {
    imei: string | null;
    iccid: string | null;
    imsi: string | null;
    callStatus: string;
    callLimit: string | null;
    asr: number | null;
    acd: number | null;
  };
}

interface ApplianceStatus {
  ts: number;
  vendor: 'yeastar' | 'dinstar';
  lines: ApplianceLine[];
  total: number;
  online: number;
  offline: number;
  busy: number;
}

const REFRESH_OPTIONS = [2, 5, 10, 30];
const ACTIVITY_INTENT: Record<ApplianceLine['activity'], 'neutral' | 'warn' | 'info' | 'bad'> = {
  idle: 'neutral',
  dialling: 'warn',
  incoming: 'info',
  active: 'bad',
  unknown: 'info',
};

/** Live trunk / port / extension state for a Yeastar or Dinstar. */
export function ApplianceLines() {
  const { id } = useParams<{ id: string }>();
  const [refreshSec, setRefreshSec] = useState(5);
  const [paused, setPaused] = useState(false);
  const [samples, setSamples] = useState<ApplianceStatus[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { data: resource } = useFetch(() => api.get<{ id: string; name: string; health: string }>(`/api/resources/${id}`), [id]);

  useEffect(() => {
    if (!id || paused) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const status = await api.get<ApplianceStatus>(`/api/plugins/voip/appliance/${id}/lines`);
        if (cancelled) return;
        setError(null);
        setLoading(false);
        setSamples((cur) => [...cur, status].slice(-120));
      } catch (err) {
        if (cancelled) return;
        setLoading(false);
        // Keep the last good sample on screen: one dropped poll isn't an outage.
        setError(err instanceof Error ? err.message : 'Could not reach the device');
      }
    };
    void tick();
    const timer = window.setInterval(() => void tick(), refreshSec * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [id, refreshSec, paused]);

  const latest = samples[samples.length - 1];
  const series = (pick: (s: ApplianceStatus) => number, name: string, intent?: 'good' | 'bad') => ({
    name,
    intent,
    points: samples.map((s) => [s.ts, pick(s)] as [number, number | null]),
  });

  if (loading && samples.length === 0 && !error) return <Skeleton height={320} />;
  if (error && samples.length === 0) return <ErrorState error={error} onRetry={() => setError(null)} />;
  if (!latest) return <EmptyState title="No data yet" text="Waiting for the first sample from the device." />;

  const showSignal = latest.lines.some((l) => l.signal !== null);
  const showGsm = latest.lines.some((l) => l.gsm);
  const dash = <span className="subtle">—</span>;

  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="page-header">
        <div className="grow">
          <div className="page-subtitle" style={{ marginBottom: 2 }}>
            <Link to={`/resources/${id}`}>
              {resource && <ReachDot health={resource.health} />}
              {resource?.name ?? 'Resource'}
            </Link>
          </div>
          <h1 className="page-title">Lines</h1>
          <div className="page-subtitle">
            {error ? (
              <span style={{ color: 'var(--itr-critical)' }}>{error} — showing last good sample</span>
            ) : (
              <>live — {new Date(latest.ts).toLocaleTimeString()}</>
            )}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }}>
            Refresh
            <select className="input" style={{ padding: '4px 8px' }} value={refreshSec} onChange={(e) => setRefreshSec(Number(e.target.value))}>
              {REFRESH_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}s
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-sm" onClick={() => setPaused((p) => !p)}>
            {paused ? <><Play size={13} /> Resume</> : <><Pause size={13} /> Pause</>}
          </button>
        </div>
      </div>

      <div className="stat-strip">
        <div className="stat-chip">
          <div className="stat-chip-label">Online</div>
          <div className="stat-chip-value" style={{ color: 'var(--itr-up)' }}>{latest.online}</div>
          <div className="stat-chip-sub">of {latest.total}</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Offline</div>
          <div className="stat-chip-value" style={{ color: latest.offline > 0 ? 'var(--itr-critical)' : undefined }}>{latest.offline}</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Busy</div>
          <div className="stat-chip-value">{latest.busy}</div>
          <div className="stat-chip-sub">in a call or ringing</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Online / busy over time</span></div>
        <div className="card-body">
          <TimeseriesChart series={[series((s) => s.online, 'Online', 'good'), series((s) => s.offline, 'Offline', 'bad'), series((s) => s.busy, 'Busy')]} height={200} />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">{latest.vendor === 'yeastar' ? 'Trunks & extensions' : 'Ports'}</span>
          <span className="grow" />
          <span className="subtle" style={{ fontSize: 12 }}>{latest.total} total</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Line</th>
                {!showGsm && <th>Type</th>}
                                <th>{showGsm ? 'On network' : 'Online'}</th>
                <th>Status</th>
                {showGsm && <th>Call</th>}
                <th>Number</th>
                <th>{showGsm ? 'Operator' : 'Carrier / type'}</th>
                {showSignal && <th title={showGsm ? 'Signal bars, as the gateway reports them' : undefined}>Signal</th>}
                {showGsm && <th>Call limit</th>}
                {showGsm && <th>IMEI</th>}
                {showGsm && <th title="SIM serial — hover a value for the IMSI">ICCID</th>}
              </tr>
            </thead>
            <tbody>
              {latest.lines.map((l) => (
                <tr key={`${l.type}-${l.id}`}>
                  <td>{l.name}</td>
                  {!showGsm && <td className="subtle">{l.type}</td>}
                  <td><Badge intent={l.online ? 'good' : 'bad'}>{l.online ? 'Y' : 'N'}</Badge></td>
                  <td><Badge intent={showGsm ? (l.online ? 'good' : 'neutral') : ACTIVITY_INTENT[l.activity]}>{l.state}</Badge></td>
                  {showGsm && <td><Badge intent={ACTIVITY_INTENT[l.activity]}>{l.gsm?.callStatus ?? '—'}</Badge></td>}
                  <td className="mono">{l.number ?? dash}</td>
                  <td>{l.carrier ?? dash}</td>
                  {showSignal && <td className="nums">{l.signal ?? dash}</td>}
                  {showGsm && <td>{l.gsm?.callLimit ? <Badge intent={/normal/i.test(l.gsm.callLimit) ? 'neutral' : 'warn'}>{l.gsm.callLimit}</Badge> : dash}</td>}
                  {showGsm && <td className="mono" style={{ whiteSpace: 'nowrap' }}>{l.gsm?.imei ?? dash}</td>}
                  {showGsm && <td className="mono" style={{ whiteSpace: 'nowrap' }} title={l.gsm?.imsi ? `IMSI ${l.gsm.imsi}` : undefined}>{l.gsm?.iccid ?? dash}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
